/**
 * Meeting Bots Admin Page
 * TASK-647: ops visibility over Attendee meeting bots after the Attendee
 * dashboard was hidden (TASK-645/646). List + cancel via the orchestrator's
 * /admin/bots proxy (gateway → orchestrator → Attendee token API).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Video, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { httpClient } from '../../lib/httpClient';

interface AttendeeBot {
  id: string;
  state: string;
  join_at: string | null;
  deduplication_key: string | null;
  metadata: { session_id?: string; account_id?: string } | null;
}

const ACTIVE_STATES = ['joining', 'joined_not_recording', 'joined_recording', 'in-call'];
const CANCELABLE_STATES = [...ACTIVE_STATES, 'ready', 'staged'];

const stateStyle = (state: string): string => {
  if (ACTIVE_STATES.includes(state)) return 'bg-green-100 text-green-800';
  if (state === 'fatal_error' || state === 'failed') return 'bg-red-100 text-red-800';
  if (state === 'ended' || state === 'left') return 'bg-gray-100 text-gray-700';
  return 'bg-yellow-100 text-yellow-800';
};

const BotsPage: React.FC = () => {
  const [bots, setBots] = useState<AttendeeBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const fetchBots = async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await httpClient.get<{ results?: AttendeeBot[] }>('/admin/bots');
      setBots(page.results || []);
    } catch (err: any) {
      console.error('Failed to load bots:', err);
      setError(err.response?.data?.detail || 'Failed to load bots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  const handleCancel = async (botId: string) => {
    setCancelling(botId);
    try {
      await httpClient.delete(`/admin/bots/${botId}`);
      toast.success(`Bot ${botId} cancelled`);
      await fetchBots();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to cancel bot');
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Video className="w-6 h-6 text-kielis-navy" />
            Meeting Bots
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Attendee bots for this install — cancel stuck or runaway bots here.
          </p>
        </div>
        <button
          onClick={fetchBots}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-kielis-navy hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-4 mb-4">
          {error}
          <button onClick={fetchBots} className="ml-3 underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-sm text-gray-500">
          Loading bots…
        </div>
      )}

      {!loading && !error && bots.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-sm text-gray-500">
          No bots. Bots appear here when a meeting is created with a Teams/Meet
          URL and auto-join enabled.
        </div>
      )}

      {!loading && bots.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bot</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meeting session</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join at</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bots.map((bot) => {
                const sessionId = bot.metadata?.session_id || bot.deduplication_key;
                const isActive = ACTIVE_STATES.includes(bot.state);
                return (
                  <tr key={bot.id} className="hover:bg-gray-50" data-testid="bot-row">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{bot.id}</td>
                    <td className="px-4 py-3 text-sm">
                      {sessionId ? (
                        isActive ? (
                          <Link to={`/meeting/${sessionId}/live`} className="text-kielis-navy underline hover:no-underline">
                            {sessionId}
                          </Link>
                        ) : (
                          <span className="text-gray-600">{sessionId}</span>
                        )
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stateStyle(bot.state)}`}>
                        {bot.state}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {bot.join_at ? new Date(bot.join_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {CANCELABLE_STATES.includes(bot.state) && (
                        <button
                          onClick={() => handleCancel(bot.id)}
                          disabled={cancelling === bot.id}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          {cancelling === bot.id ? 'Cancelling…' : 'Cancel'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BotsPage;
